import { pdfjs } from './pdfjs'
import type { PageText, TextRun } from './placement'

/**
 * Past this many pages the document is a manual rather than a contract, and
 * reading every page would keep the worker busy for nothing a sender will act
 * on.
 */
const MAX_PAGES = 200

/**
 * The text of every page, positioned in the same space field boxes use.
 *
 * Coordinates are fractions of the unscaled, rotated viewport with the origin
 * at the top left — the space a sender drags in — so a run found here and a box
 * placed by hand can be compared without converting either.
 */
export async function readDocumentText(url: string): Promise<PageText[]> {
  const task = pdfjs.getDocument({ url, withCredentials: true })
  try {
    const doc = await task.promise
    const pages: PageText[] = []
    for (let n = 1; n <= Math.min(doc.numPages, MAX_PAGES); n++) {
      const page = await doc.getPage(n)
      const viewport = page.getViewport({ scale: 1 })
      const content = await page.getTextContent()
      const runs: TextRun[] = []
      for (const item of content.items) {
        if (!('str' in item) || !item.str.trim()) continue
        const tx = pdfjs.Util.transform(viewport.transform, item.transform)
        const run = shownRun(item.str, item.width, tx, viewport.width, viewport.height)
        if (run) runs.push(run)
      }
      pages.push({ page: n, runs })
      page.cleanup()
    }
    return pages
  } finally {
    void task.destroy()
  }
}

/**
 * How far a run's baseline may lean from the shown page's left-to-right axis
 * and still be read: about ten degrees, a scan placed a little crooked.
 */
const MAX_LEAN = Math.tan((10 * Math.PI) / 180)

/**
 * One run of text as the shown page has it, or null when it does not read
 * left to right there.
 *
 * `tx` is the run's matrix on the shown page at scale 1 (the viewport's
 * transform times the run's), and `width` its length along its own baseline.
 * The placement rules read a run as a line of text running to the right, one
 * character after another, so a run is only useful to them when it does. A
 * page turned a quarter (/Rotate 90 or 270) whose text was drawn for the page
 * upright reads sideways once turned: its length runs down the shown page,
 * and it used to be divided by the shown width and laid along the shown x
 * axis, which put its label, and any blank of underscores in it, across the
 * page where nothing is written. A suggestion there is worse than none.
 * Text drawn to read upright on the turned page is unaffected, and is most
 * text on such a page.
 */
export function shownRun(
  str: string,
  width: number,
  tx: number[],
  shownWidth: number,
  shownHeight: number,
): TextRun | null {
  const [a, b] = tx
  if (!(a > 0) || Math.abs(b) > a * MAX_LEAN) return null
  return {
    str,
    x: tx[4] / shownWidth,
    y: tx[5] / shownHeight,
    w: width / shownWidth,
    h: Math.hypot(tx[2], tx[3]) / shownHeight,
  }
}
